import { 
  doc as firestoreDoc, 
  getDoc, 
  updateDoc, 
  collection, 
  addDoc, 
  getDocs,
  deleteDoc, 
  query, 
  orderBy,
  setDoc,
  where,
  writeBatch,
  serverTimestamp,
  increment,
  limit
} from 'firebase/firestore';
import { db, formatDocument } from './firebase';

/**
 * Busca todos os profissionais de um salão
 * @param {string} salonId - ID do salão
 * @returns {Promise<Array>} - Lista de profissionais
 */
export const getProfessionals = async (salonId) => {
  try {
    console.log("[getProfessionals] Buscando profissionais para salonId:", salonId);
    
    const professionalsRef = collection(db, `salons/${salonId}/professionals`);
    const professionalsSnapshot = await getDocs(query(professionalsRef, orderBy('nome')));
    
    const professionals = professionalsSnapshot.docs.map(doc => formatDocument(doc) || {
      id: doc.id,
      ...doc.data()
    });
    
    console.log(`[getProfessionals] Encontrados ${professionals.length} profissionais:`, 
      professionals.map(p => ({ id: p.id, nome: p.nome || p.name })));
    
    return professionals;
  } catch (error) {
    console.error('[getProfessionals] Erro ao buscar profissionais:', error);
    throw error;
  }
};

/**
 * Busca um profissional específico
 * @param {string} salonId - ID do salão
 * @param {string} professionalId - ID do profissional
 * @returns {Promise<object|null>} - Dados do profissional ou null se não encontrado
 */
export const getProfessional = async (salonId, professionalId) => {
  try {
    const professionalRef = firestoreDoc(db, `salons/${salonId}/professionals/${professionalId}`);
    const professionalSnap = await getDoc(professionalRef);
    
    if (professionalSnap.exists()) {
      return formatDocument(professionalSnap) || {
        id: professionalSnap.id,
        ...professionalSnap.data()
      };
    } else {
      console.error('Profissional não encontrado');
      return null;
    }
  } catch (error) {
    console.error('Erro ao buscar profissional:', error);
    throw error;
  }
};

/**
 * Adiciona um novo profissional
 * @param {string} salonId - ID do salão
 * @param {object} professionalData - Dados do profissional
 * @returns {Promise<object>} - Profissional criado com ID
 */
export const addProfessional = async (salonId, professionalData) => {
  try {
    const professionalsRef = collection(db, `salons/${salonId}/professionals`);
    const docRef = await addDoc(professionalsRef, {
      ...professionalData,
      serviceCount: 0, // Garantir que sempre tenha serviceCount
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return { 
      id: docRef.id, 
      ...professionalData,
      serviceCount: 0,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  } catch (error) {
    console.error('Erro ao adicionar profissional:', error);
    throw error;
  }
};

/**
 * Atualiza um profissional existente
 * @param {string} salonId - ID do salão
 * @param {string} professionalId - ID do profissional
 * @param {object} professionalData - Novos dados do profissional
 * @returns {Promise<object>} - Profissional atualizado
 */
export const updateProfessional = async (salonId, professionalId, professionalData) => {
  try {
    const professionalRef = firestoreDoc(db, `salons/${salonId}/professionals/${professionalId}`);
    
    // Não sobrescrever serviceCount se ele não for fornecido
    const dataToUpdate = { ...professionalData };
    if (!dataToUpdate.hasOwnProperty('serviceCount')) {
      delete dataToUpdate.serviceCount;
    }
    
    await updateDoc(professionalRef, {
      ...dataToUpdate,
      updatedAt: serverTimestamp()
    });
    
    return { 
      id: professionalId, 
      ...professionalData,
      updatedAt: new Date()
    };
  } catch (error) {
    console.error('Erro ao atualizar profissional:', error);
    throw error;
  }
};

/**
 * Remove um profissional
 * @param {string} salonId - ID do salão
 * @param {string} professionalId - ID do profissional
 * @returns {Promise<boolean>} - True se removido com sucesso
 */
export const deleteProfessional = async (salonId, professionalId) => {
  try {
    console.log(`[deleteProfessional] Iniciando exclusão do profissional: ${professionalId}`);
    
    // Verificar se o profissional existe
    const professionalRef = firestoreDoc(db, `salons/${salonId}/professionals/${professionalId}`);
    const professionalSnap = await getDoc(professionalRef);
    
    if (!professionalSnap.exists()) {
      console.warn(`[deleteProfessional] Profissional não encontrado: ${professionalId}`);
      return false;
    }
    
    const professional = {
      id: professionalSnap.id,
      ...professionalSnap.data()
    };
    
    console.log(`[deleteProfessional] Profissional encontrado: ${professional.nome || professional.name}`);
    
    const batch = writeBatch(db);
    
    // 1. Obter todas as associações deste profissional
    const associationsQuery = query(
      collection(db, `salons/${salonId}/professional_services`),
      where('professionalId', '==', professionalId)
    );
    const associationsSnapshot = await getDocs(associationsQuery);
    
    console.log(`[deleteProfessional] Encontradas ${associationsSnapshot.docs.length} associações para remover`);
    
    // 2. Atualizar contadores de serviços e remover associações
    const servicesRef = {};
    const serviceIds = [];
    
    associationsSnapshot.docs.forEach(doc => {
      const data = doc.data();
      batch.delete(doc.ref);
      
      // Armazenar referências para atualizar contadores de serviços
      if (!servicesRef[data.serviceId]) {
        servicesRef[data.serviceId] = firestoreDoc(db, `salons/${salonId}/services/${data.serviceId}`);
        serviceIds.push(data.serviceId);
      }
    });
    
    // 3. Decrementar professionalCount para cada serviço
    for (const serviceId in servicesRef) {
      try {
        // Verificar o serviço antes de atualizar
        const serviceSnap = await getDoc(servicesRef[serviceId]);
        
        if (serviceSnap.exists()) {
          const service = serviceSnap.data();
          const currentCount = service.professionalCount || 0;
          
          // Evitar contador negativo
          const newCount = Math.max(0, currentCount - 1);
          
          console.log(`[deleteProfessional] Atualizando professionalCount para serviço ${serviceId}: ${currentCount} -> ${newCount}`);
          
          batch.update(servicesRef[serviceId], {
            professionalCount: newCount,
            atualizado_em: serverTimestamp()
          });
        } else {
          console.warn(`[deleteProfessional] Serviço não encontrado para atualização: ${serviceId}`);
        }
      } catch (err) {
        console.error(`[deleteProfessional] Erro ao atualizar contador do serviço ${serviceId}:`, err);
        // Continuar mesmo com erro para não bloquear a operação completa
      }
    }
    
    // 4. Remover o profissional
    batch.delete(professionalRef);
    
    console.log(`[deleteProfessional] Executando batch com ${Object.keys(servicesRef).length} atualizações de serviços`);
    await batch.commit();
    
    console.log(`[deleteProfessional] Profissional ${professionalId} removido com sucesso`);
    return true;
  } catch (error) {
    console.error('[deleteProfessional] Erro ao excluir profissional:', error);
    throw error;
  }
};

/**
 * Busca a disponibilidade de um profissional
 * @param {string} salonId - ID do salão
 * @param {string} professionalId - ID do profissional
 * @returns {Promise<object|null>} - Dados de disponibilidade ou null se não encontrados
 */
export const getProfessionalAvailability = async (salonId, professionalId) => {
  try {
    const availabilityRef = firestoreDoc(db, `salons/${salonId}/professionals/${professionalId}/availability/config`);
    const availabilitySnap = await getDoc(availabilityRef);
    
    if (availabilitySnap.exists()) {
      return {
        id: availabilitySnap.id,
        ...availabilitySnap.data()
      };
    } else {
      // Retornar configuração padrão se não existir
      return {
        workDays: [1, 2, 3, 4, 5], // Segunda a sexta
        workHours: {
          start: '09:00',
          end: '18:00'
        },
        breakTime: {
          start: '12:00',
          end: '13:00'
        }
      };
    }
  } catch (error) {
    console.error('Erro ao buscar disponibilidade do profissional:', error);
    throw error;
  }
};

/**
 * Atualiza a disponibilidade de um profissional
 * @param {string} salonId - ID do salão
 * @param {string} professionalId - ID do profissional
 * @param {object} availabilityData - Dados de disponibilidade
 * @returns {Promise<object>} - Disponibilidade atualizada
 */
export const updateProfessionalAvailability = async (salonId, professionalId, availabilityData) => {
  try {
    const availabilityRef = firestoreDoc(db, `salons/${salonId}/professionals/${professionalId}/availability/config`);
    const availabilitySnap = await getDoc(availabilityRef);
    
    if (availabilitySnap.exists()) {
      await updateDoc(availabilityRef, {
        ...availabilityData,
        updatedAt: serverTimestamp()
      });
    } else {
      await setDoc(availabilityRef, {
        ...availabilityData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
    
    return availabilityData;
  } catch (error) {
    console.error('Erro ao atualizar disponibilidade do profissional:', error);
    throw error;
  }
};

/**
 * Busca um profissional pelo email
 * @param {string} salonId - ID do salão
 * @param {string} email - Email do profissional
 * @returns {Promise<object|null>} - Dados do profissional ou null se não encontrado
 */
export const getProfessionalByEmail = async (salonId, email) => {
  try {
    console.log(`[getProfessionalByEmail] Buscando profissional com email: ${email} no salão: ${salonId}`);
    
    if (!email) {
      console.warn('[getProfessionalByEmail] Email não fornecido');
      return null;
    }
    
    // Normalizar email para evitar problemas de case sensitivity
    const normalizedEmail = email.toLowerCase();
    
    const professionalsRef = collection(db, `salons/${salonId}/professionals`);
    const q = query(
      professionalsRef, 
      where('email', '==', normalizedEmail),
      limit(1)
    );
    
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      console.log(`[getProfessionalByEmail] Nenhum profissional encontrado com email: ${email}`);
      return null;
    }
    
    const professionalDoc = snapshot.docs[0];
    const professional = formatDocument(professionalDoc) || {
      id: professionalDoc.id,
      ...professionalDoc.data()
    };
    
    console.log(`[getProfessionalByEmail] Profissional encontrado: ${professional.id}`);
    return professional;
  } catch (error) {
    console.error('[getProfessionalByEmail] Erro ao buscar profissional por email:', error);
    throw error;
  }
};

// NOTA: A função getProfessionalServices foi movida para o arquivo professionalServices.js
// NOTA: A função updateProfessionalServices foi substituída por batchUpdateProfessionalServiceAssociations em professionalServices.js 